import { useEffect, useState } from "react";
import { fitWidth, FALLBACK_ASPECT, previewBudget } from "./pdfFit";

// Ниже этого размера измерение области считаем недостоверным (элемент ещё не
// в лейауте / схлопнулся) и откатываемся на бюджет от окна.
const MIN_SANE_BOX_PX = 80;

/**
 * Отдаёт размеры, при которых текущая страница PDF целиком помещается в
 * отведённую под превью область.
 *
 * Соотношение сторон берётся РЕАЛЬНОЕ — из самого документа через уже
 * открытый `pdfProxy` (PDFDocumentProxy из Document.onLoadSuccess, без
 * повторного скачивания/парсинга файла). Раньше ширина считалась по формуле,
 * жёстко предполагавшей A4, и нестандартный документ (чек, длинная
 * квитанция) не помещался по высоте и разъезжался поверх интерфейса.
 *
 * Бюджет области: измеряем `boxRef` — тот самый внутренний блок
 * (`flex-1 min-h-0 w-full ... overflow-hidden`), в который и вписывается
 * страница. Его размер задаёт флекс-раскладка карточки, а НЕ содержимое
 * (min-h-0 + overflow-hidden), поэтому цикла «меряю контейнер, чтобы
 * посчитать его же содержимое» тут нет.
 *
 * Мерить именно этот блок, а не внешний контейнер превью, принципиально:
 * контейнер превью в EditDoc.jsx (`.ml-[1vw] mt-[2vh] ... flex flex-row`)
 * своей ширины не имеет и подстраивается под содержимое — при попытке
 * измерить его ширина схлопывалась до величины заглушки (замерено — около
 * 65px вместо ожидаемых ~486px).
 *
 * `boxEl` — сам DOM-узел, а не ref-объект: блок появляется только после
 * загрузки PDF (до этого компонент рендерит лоадер и выходит раньше), и с
 * ref-объектом эффект измерения так и остался бы висеть на null — ref не
 * меняет идентичность, пересчёта бы не случилось. Узел в state (callback-ref)
 * перезапускает эффект ровно тогда, когда блок реально появился.
 *
 * Если узел не передан или измерение ещё недостоверно (элемент не в лейауте,
 * размер меньше MIN_SANE_BOX_PX) — откатываемся на прежний бюджет от размера
 * окна (previewBudget). Так поведение при первом кадре и в отсутствие
 * ResizeObserver остаётся ровно прежним.
 *
 * Зачем вообще измерять: высота области превью не постоянна. На экране
 * параметров под ней могут появиться плашки («мало бумаги», «аппарат не
 * принимает заказы»), и карточка становится ниже. При бюджете от окна
 * (0.5×innerHeight) страница сохраняла прежнюю высоту и обрезалась по
 * нижнему краю карточки.
 *
 * pageAspect === null, пока соотношение сторон страницы ещё не получено —
 * вызывающий код в это время показывает лоадер, а не рендерит <Page> с
 * заведомо неверной шириной.
 */
export function usePdfPageFit(pdfProxy, pageNumber, boxEl) {
    const [windowBudget, setWindowBudget] = useState(() => previewBudget());
    const [measuredBox, setMeasuredBox] = useState(null);
    const [pageAspect, setPageAspect] = useState(null);

    useEffect(() => {
        const onResize = () => setWindowBudget(previewBudget());
        window.addEventListener("resize", onResize);
        window.addEventListener("orientationchange", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("orientationchange", onResize);
        };
    }, []);

    useEffect(() => {
        if (!boxEl || typeof ResizeObserver === "undefined") {
            setMeasuredBox(null);
            return undefined;
        }

        const measure = () => {
            const { width, height } = boxEl.getBoundingClientRect();
            setMeasuredBox(
                width >= MIN_SANE_BOX_PX && height >= MIN_SANE_BOX_PX
                    ? { width, height }
                    : null,
            );
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(boxEl);
        return () => observer.disconnect();
    }, [boxEl]);

    const budget = measuredBox || windowBudget;

    useEffect(() => {
        if (!pdfProxy) {
            setPageAspect(null);
            return undefined;
        }
        let cancelled = false;
        setPageAspect(null); // при смене страницы — снова "не знаем", пока не спросили pdf.js
        pdfProxy
            .getPage(pageNumber)
            .then((page) => {
                if (cancelled) return;
                const viewport = page.getViewport({ scale: 1 });
                setPageAspect(viewport.width / viewport.height);
            })
            .catch(() => {
                if (!cancelled) setPageAspect(FALLBACK_ASPECT);
            });
        return () => {
            cancelled = true;
        };
    }, [pdfProxy, pageNumber]);

    const ready = pageAspect !== null;
    const pageWidth = ready ? fitWidth(pageAspect, budget.width, budget.height) : 0;
    const pageHeight = ready ? pageWidth / pageAspect : 0;

    return { box: budget, pageAspect, pageWidth, pageHeight, ready };
}
